/**
 * TreatX Admin - Category Management Page
 * Full CRUD for treatment categories
 * P2.3b: Full i18n coverage
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTreatXAdminAuth } from '../../context/TreatXAdminAuthContext';
import { useCategoryManagement, AVAILABLE_ICONS } from '../../context/CategoryManagementContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { computeFieldCompleteness } from '../../context/LanguageContext';
import { Textarea } from '../../components/ui/textarea';
import {
  Shield, LogOut, Plus, Search, Edit2, Trash2, ArrowLeft,
  Eye, EyeOff, Save, X, Image as ImageIcon, Grid3X3,
  CheckCircle, Stethoscope, Heart, Smile, Scissors, Sparkles,
  Baby, Activity, Pill, Scale, Sun, User, Brain
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/Toaster';
import { ImageUploadField } from '../../components/ImageUploadField';
import { MultilingualInput } from '../../components/MultilingualInput';
import { getLocalizedField } from '../../context/LanguageContext';

// Icon component mapper
const IconComponent = ({ iconId, className }) => {
  const iconMap = {
    stethoscope: Stethoscope,
    heart: Heart,
    smile: Smile,
    eye: Eye,
    scissors: Scissors,
    sparkles: Sparkles,
    baby: Baby,
    bone: Shield,
    brain: Brain,
    activity: Activity,
    pill: Pill,
    scale: Scale,
    user: User,
    sun: Sun
  };
  const Icon = iconMap[iconId] || Grid3X3;
  return <Icon className={className} />;
};

// Category Edit Modal
const CategoryEditModal = ({ category, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: category?.name || { en: '', tr: '', de: '', es: '', ar: '', ru: '', fr: '' },
    icon: category?.icon || 'stethoscope',
    emoji: category?.emoji || '',
    image: category?.image_url || category?.image || '',
    media_id: category?.media_id || null,
    description: category?.description || { en: '', tr: '', de: '', es: '', ar: '', ru: '', fr: '' },
    status: category?.status || 'active'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const enName = typeof formData.name === 'string' ? formData.name : (formData.name?.en || '');
    if (!enName.trim()) {
      toast({ title: 'Error', description: 'Category name (English) is required', variant: 'destructive' });
      return;
    }
    onSave(formData);
  };

  const selectedIcon = AVAILABLE_ICONS.find(i => i.id === formData.icon);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">
                {category ? 'Edit Category' : 'Create New Category'}
              </h2>
              <p className="text-purple-200 text-sm mt-1">
                {category ? `Editing: ${getLocalizedField(category.name, 'en')}` : 'Define a new treatment category'}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/20">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto max-h-[60vh]">
          {/* Category Name */}
          <div>
            <MultilingualInput
              value={formData.name}
              onChange={(val) => setFormData({ ...formData, name: val })}
              label="Category Name"
              required
              placeholder="e.g., Dental, Cosmetic Surgery, Hair Restoration"
              policy="translatable_required"
            />
          </div>

          {/* Icon Selection */}
          <div>
            <Label className="mb-3 block">Select Icon *</Label>
            <div className="grid grid-cols-7 gap-2">
              {AVAILABLE_ICONS.map((icon) => (
                <button
                  key={icon.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, icon: icon.id, emoji: icon.emoji })}
                  className={`p-3 rounded-lg border-2 text-center transition-all ${
                    formData.icon === icon.id
                      ? 'border-purple-600 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  title={icon.name}
                >
                  <div className="text-2xl">{icon.emoji}</div>
                </button>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Selected: {selectedIcon?.name || 'None'} {selectedIcon?.emoji}
            </p>
          </div>

          {/* Category Image */}
          <div>
            <Label className="mb-2 block">Category Image</Label>
            <ImageUploadField
              currentImage={formData.image}
              mediaId={formData.media_id}
              assetType="category_image"
              onUploadComplete={(asset) => {
                setFormData(prev => ({
                  ...prev,
                  image: asset.public_url || asset.signed_url || prev.image,
                  media_id: asset.id,
                }));
              }}
              onClear={() => setFormData(prev => ({ ...prev, image: '', media_id: null }))}
            />
          </div>

          {/* Description */}
          <div>
            <MultilingualInput
              value={formData.description}
              onChange={(val) => setFormData({ ...formData, description: val })}
              label="Description"
              multiline
              placeholder="Brief description of treatments in this category..."
              policy="translatable_optional"
            />
          </div>

          {/* Status */}
          <div>
            <Label className="mb-3 block">Status</Label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, status: 'active' })}
                className={`flex-1 p-4 rounded-lg border-2 ${
                  formData.status === 'active' 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-gray-200'
                }`}
              >
                <Eye className="h-5 w-5 mx-auto mb-2 text-green-600" />
                <p className="font-medium">Active</p>
                <p className="text-xs text-gray-500">Visible everywhere</p>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, status: 'draft' })}
                className={`flex-1 p-4 rounded-lg border-2 ${
                  formData.status === 'draft' 
                    ? 'border-amber-500 bg-amber-50' 
                    : 'border-gray-200'
                }`}
              >
                <EyeOff className="h-5 w-5 mx-auto mb-2 text-amber-600" />
                <p className="font-medium">Draft</p>
                <p className="text-xs text-gray-500">Hidden from public</p>
              </button>
            </div>
          </div>

          {/* Preview */}
          <Card className="bg-slate-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center text-2xl">
                  {formData.emoji}
                </div>
                <div>
                  <p className="font-semibold">{getLocalizedField(formData.name, 'en') || 'Category Name'}</p>
                  <p className="text-sm text-gray-500">{getLocalizedField(formData.description, 'en') || 'No description'}</p>
                </div>
                <Badge className={formData.status === 'active' ? 'bg-green-100 text-green-700 ml-auto' : 'bg-amber-100 text-amber-700 ml-auto'}>
                  {formData.status}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600">
              <Save className="h-4 w-4 mr-2" />
              {category ? 'Update Category' : 'Create Category'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CategoryManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { admin, logout } = useTreatXAdminAuth();
  const { 
    categories, 
    getAllCategories, 
    addCategory, 
    updateCategory, 
    deleteCategory 
  } = useCategoryManagement();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editModal, setEditModal] = useState({ show: false, category: null });

  // Redirect if not logged in
  if (!admin) {
    navigate('/treatx-admin/login');
    return null;
  }

  const allCategories = getAllCategories();

  // Filter categories
  const filteredCategories = allCategories.filter(c => {
    const catName = getLocalizedField(c.name, 'en');
    const matchesSearch = catName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    total: allCategories.length,
    active: allCategories.filter(c => c.status === 'active').length,
    draft: allCategories.filter(c => c.status === 'draft').length,
    withTreatments: allCategories.filter(c => c.treatmentCount > 0).length
  };

  const handleSaveCategory = (data) => {
    const displayName = getLocalizedField(data.name, 'en');
    if (editModal.category) {
      updateCategory(editModal.category.id, data);
      toast({ title: 'Category Updated', description: `"${displayName}" has been updated.` });
    } else {
      addCategory(data);
      toast({ title: 'Category Created', description: `"${displayName}" has been created.` });
    }
    setEditModal({ show: false, category: null });
  };

  const handleDeleteCategory = (category) => {
    if (category.treatmentCount > 0) {
      toast({ 
        title: 'Cannot Delete', 
        description: `This category has ${category.treatmentCount} treatments. Remove treatments first.`,
        variant: 'destructive'
      });
      return;
    }
    deleteCategory(category.id);
    toast({ title: 'Category Deactivated', description: `"${getLocalizedField(category.name, 'en')}" has been moved to drafts.` });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-700 via-pink-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate('/treatx-admin/dashboard')}
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Category Management</h1>
                <p className="text-purple-200">Create and manage treatment categories</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right mr-4">
                <p className="font-medium">{admin?.name}</p>
                <p className="text-sm text-purple-200">Platform Administrator</p>
              </div>
              <Button variant="ghost" size="icon" onClick={logout} className="text-white hover:bg-white/20">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="bg-white/10 backdrop-blur">
          <div className="max-w-7xl mx-auto px-4 py-4 grid grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-3xl font-bold">{stats.total}</p>
              <p className="text-sm text-purple-200">Total Categories</p>
            </div>
            <div>
              <p className="text-3xl font-bold">{stats.active}</p>
              <p className="text-sm text-purple-200">Active</p>
            </div>
            <div>
              <p className="text-3xl font-bold">{stats.draft}</p>
              <p className="text-sm text-purple-200">Drafts</p>
            </div>
            <div>
              <p className="text-3xl font-bold">{stats.withTreatments}</p>
              <p className="text-sm text-purple-200">With Treatments</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Treatment Categories</CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Define categories for organizing treatments
                </p>
              </div>
              <Button 
                onClick={() => setEditModal({ show: true, category: null })}
                className="bg-gradient-to-r from-purple-600 to-pink-600"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="flex gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search categories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border rounded-lg px-4 py-2 min-w-[150px]"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            {/* Categories Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCategories.map((category) => (
                <Card key={category.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  {/* Category Image */}
                  <div className="h-32 bg-gradient-to-br from-purple-100 to-pink-100 relative">
                    {category.image && (
                      <img
                        src={category.image}
                        alt={getLocalizedField(category.name, 'en')}
                        className="w-full h-full object-cover"
                      />
                    )}
                    <div className="absolute top-2 right-2">
                      <Badge className={category.status === 'active' ? 'bg-green-500' : 'bg-amber-500'}>
                        {category.status}
                      </Badge>
                    </div>
                    <div className="absolute bottom-2 left-2 w-12 h-12 rounded-lg bg-white shadow-lg flex items-center justify-center text-2xl">
                      {category.emoji}
                    </div>
                  </div>
                  
                  {/* Category Info */}
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg">{getLocalizedField(category.name, 'en')}</h3>
                        <p className="text-sm text-gray-500 line-clamp-2">{getLocalizedField(category.description, 'en')}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">
                          {category.treatmentCount} treatments
                        </Badge>
                        {(() => {
                          const comp = computeFieldCompleteness(category.name);
                          const color = comp.filled === comp.total ? 'bg-green-50 text-green-600 border-green-200'
                            : comp.filled > 1 ? 'bg-amber-50 text-amber-600 border-amber-200'
                            : 'bg-blue-50 text-blue-600 border-blue-200';
                          return (
                            <Badge variant="outline" className={`text-[9px] ${color}`} data-testid="translation-badge">
                              {comp.filled}/{comp.total} langs
                            </Badge>
                          );
                        })()}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditModal({ show: true, category })}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteCategory(category)}
                          className="text-red-500 hover:text-red-700"
                          disabled={category.treatmentCount > 0}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {filteredCategories.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Grid3X3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No categories found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Modal */}
      {editModal.show && (
        <CategoryEditModal
          category={editModal.category}
          onSave={handleSaveCategory}
          onClose={() => setEditModal({ show: false, category: null })}
        />
      )}
    </div>
  );
};

export default CategoryManagement;
